/**
 * Quem está logado — uma pergunta, uma resposta, para o app inteiro.
 *
 * ═══ POR QUE CONTEXTO, E NÃO CADA TELA PERGUNTANDO ═══
 *
 * São 24 telas. Se cada uma perguntasse "quem sou eu?" ao abrir, seriam
 * 24 chamadas iguais, e cada troca de tela pagaria uma ida ao servidor
 * para saber algo que não mudou. Pior: durante essa ida a tela não sabe
 * se pode desenhar, então piscaria entre vazio e conteúdo a cada
 * navegação.
 *
 * A pergunta é feita UMA vez, na subida, e a resposta fica aqui.
 */

import {
  createContext, useCallback, useContext, useEffect, useState,
  type ReactNode,
} from 'react'
import { api } from '../api/cliente'

export interface Usuario {
  id: number
  nome: string
  email: string
  papel: 'dono' | 'gerente' | 'operador'
  empresa_id: number
}

interface Sessao {
  usuario: Usuario | null
  /**
   * O que esta empresa contratou. Hoje é `{ filiais: boolean }`.
   *
   * Vem do servidor a cada carga, e não de um ajuste local: quem decide
   * o que um cliente enxerga é quem vende, no painel interno.
   */
  recursos: Record<string, boolean>
  /** Instalação nova, sem usuário nenhum: a tela pede para criar o dono. */
  primeiroAcesso: boolean
  /** Enquanto não se sabe, não se decide nada — ver `Porteiro`. */
  carregando: boolean
  entrar: (usuario: Usuario) => void
  sair: () => Promise<void>
}

const Contexto = createContext<Sessao>({
  usuario: null, recursos: {}, primeiroAcesso: false, carregando: true,
  entrar: () => {}, sair: async () => {},
})

export function usarSessao() {
  return useContext(Contexto)
}

/** Para onde a pessoa vai depois de sair.
 *
 * ═══ O SITE, E NÃO A TELA DE LOGIN ═══
 *
 * Quem sai do painel terminou o que veio fazer. Devolvê-lo à tela de
 * login oferece a porta que ele acabou de fechar — e num computador de
 * portaria isso é um convite para a próxima pessoa tentar entrar.
 * `www.recognis.com.br` é a saída certa: fora do sistema, sem sessão
 * nenhuma à vista.
 *
 * ⚠️ MENOS EM DESENVOLVIMENTO. Em `localhost` isso mandaria quem está
 * programando para o site público a cada logout, e o caminho de volta
 * seria digitar o endereço na mão. Ali a tela de login é o destino
 * certo, porque é justamente o que se está testando.
 */
function destinoAoSair(): string {
  const h = window.location.hostname
  const local = h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')
  return local ? '/' : 'https://www.recognis.com.br'
}


export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [recursos, setRecursos] = useState<Record<string, boolean>>({})
  const [primeiroAcesso, setPrimeiro] = useState(false)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    let vivo = true
    api.ler<{ usuario: Usuario | null; primeiro_acesso: boolean
              recursos?: Record<string, boolean> }>('/api/eu')
      .then(d => {
        if (!vivo) return
        setUsuario(d.usuario)
        setRecursos(d.recursos ?? {})
        setPrimeiro(d.primeiro_acesso)
      })
      /* Servidor fora do ar não é "não logado": mandar para a tela de
         entrada aqui faria a pessoa digitar a senha para receber o mesmo
         erro. Fica sem usuário e a tela de entrada mostra o motivo. */
      .catch(() => {})
      .finally(() => { if (vivo) setCarregando(false) })
    return () => { vivo = false }
  }, [])

  const entrar = useCallback((u: Usuario) => {
    setUsuario(u)
    setPrimeiro(false)
    /* Relê os recursos: a resposta do login já os traz, mas quem chama
       `entrar` é a tela de entrada, que só repassa o usuário. Sem esta
       leitura, quem acabou de entrar veria o menu sem Filiais até
       recarregar a página. */
    api.ler<{ recursos?: Record<string, boolean> }>('/api/eu')
      .then(d => setRecursos(d.recursos ?? {}))
      .catch(() => {})
  }, [])

  const sair = useCallback(async () => {
    try { await api.criar('/api/sair', {}) } finally {
      setUsuario(null)
      /* Recarrega a página inteira, e não só troca o estado. As telas
         guardam listas, filtros e imagens da pessoa que estava logada;
         trocar só o `usuario` deixaria tudo isso na memória, à vista de
         quem entrar em seguida no mesmo computador — que num posto de
         portaria é o caso normal, não a exceção. */
      window.location.replace(destinoAoSair())
    }
  }, [])

  return (
    <Contexto.Provider
      value={{ usuario, recursos, primeiroAcesso, carregando, entrar, sair }}>
      {children}
    </Contexto.Provider>
  )
}
